import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem('eam_theme');
      return saved === 'light' || saved === 'dark' ? saved : 'dark';
    } catch (e) {
      return 'dark';
    }
  });
  const transitionTimer = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem('eam_theme', theme);
    } catch (e) {
      /* storage unavailable (private mode) */
    }
    const root = document.documentElement;

    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    } else {
      root.classList.remove('light-theme');
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    }
    root.dataset.theme = theme;

    // Update browser chrome color
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.content = theme === 'light' ? '#f8f9fc' : '#0a0a0a';
    }
  }, [theme]);

  useEffect(() => () => window.clearTimeout(transitionTimer.current), []);

  // Smooth color cross-fade only while switching (see `html.theme-transition` in index.css)
  const withTransition = useCallback((fn) => {
    const root = document.documentElement;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      root.classList.add('theme-transition');
      window.clearTimeout(transitionTimer.current);
      transitionTimer.current = window.setTimeout(() => root.classList.remove('theme-transition'), 450);
    }
    fn();
  }, []);

  const setTheme = useCallback(
    (next) => withTransition(() => setThemeState(next)),
    [withTransition]
  );

  const toggleTheme = useCallback(() => {
    withTransition(() => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')));
  }, [withTransition]);

  const isDark = theme === 'dark';

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, isDark }),
    [theme, setTheme, toggleTheme, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
